"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { PricingTable } from "@/components/pricing";
import type { TBillingInterval } from "@/components/pricing";
import type { TPricingTier } from "@/lib/sanity/pricing";
import { AnimatedButton } from "@/components/marketing/AnimatedButton";
import { MotionReveal } from "@/components/marketing/MotionReveal";
import { PhosphorIcon } from "@/components/marketing/PhosphorIcon";
import { trackAnalyticsEvent } from "@/lib/analytics/track";

interface IPricingSectionProps {
  tiers: TPricingTier[];
  subscriptionCounts?: Record<string, number>;
  /** When false, CTA buttons show "Join Waitlist" and link to the waitlist form. */
  portalOpen?: boolean;
}

/**
 * Client section: interval toggle state + PricingTable. CTA navigates to signup
 * when the portal is open, or to the waitlist when closed.
 */
export function PricingSection({
  tiers,
  subscriptionCounts,
  portalOpen = true,
}: IPricingSectionProps): React.ReactElement {
  const router = useRouter();
  const [interval, setInterval] = useState<TBillingInterval>("monthly");
  const viewedPricing = useRef(false);

  const ctaTarget = portalOpen ? "/signup" : "/#waitlist";

  useEffect(() => {
    if (viewedPricing.current) return;
    viewedPricing.current = true;
    trackAnalyticsEvent("view_pricing", { surface: "marketing_pricing" });
  }, []);

  function handleSelectPlan(slug: string, selectedInterval: TBillingInterval): void {
    trackAnalyticsEvent("select_plan", {
      plan_slug: slug,
      billing_interval: selectedInterval,
      surface: "marketing_pricing",
    });
    router.push(ctaTarget);
  }

  const displayTiers = useMemo(
    () =>
      portalOpen
        ? tiers
        : tiers.map((t) => ({ ...t, ctaText: "Join Waitlist" })),
    [tiers, portalOpen],
  );

  return (
    <>
      <motion.div layout transition={{ duration: 0.25 }}>
        <PricingTable
          tiers={displayTiers}
          interval={interval}
          onIntervalChange={setInterval}
          onSelectPlan={handleSelectPlan}
          showCta={tiers.length > 0}
          subscriptionCounts={subscriptionCounts}
        />
      </motion.div>
      <div className="mt-12">
        <MotionReveal className="flex items-center justify-center gap-3">
          <PhosphorIcon
            name="crown"
            className="h-8 w-8 text-primary"
            aria-hidden="true"
          />
          <AnimatedButton>
            <Button asChild size="lg">
              <Link href={ctaTarget}>
                {portalOpen ? "Get started" : "Join Waitlist"}
              </Link>
            </Button>
          </AnimatedButton>
        </MotionReveal>
      </div>
    </>
  );
}
